import { useTranslation } from "react-i18next";
import type { BillingCycle } from "../../../api/hooks/useTradingStrategies";
import { formatPriceUsd } from "../../../lib/money";

// Every surface that prices a strategy renders it here, so a price reads the same way on a browse
// card, a strategy page, a subscription and the dashboard cross-sell. Three shapes, one rule set:
// zero says "Free", a comped subscription says "Staff managed", and everything else weights the
// amount and lets the cycle sit quietly beside it.
//
// The cycle is set quieter than the figure because it is the unit on the number rather than part of
// it — "$55/mo" in one bold run makes "/mo" shout as loudly as the price. The shapes that can style
// the two apart do; the text shape can't, so it says "$55 / month" and leaves it there. Nothing
// restates the cycle in brackets afterwards: the label above a headline already names it.
//
// Freeness is the caller's decision, not this component's, because the two sides ask different
// questions. The catalogue asks whether *both* cycles are zero — a strategy free one way and priced
// the other is a pricing mistake, and saying Free would hide it. A subscription asks only what this
// customer is charged, which is a single number captured when they subscribed.

interface StrategyPriceProps {
  /** Say "Free" rather than an amount. See the note above on why the caller decides this. */
  free: boolean;
  /** Null, with a null cycle, for a staff-managed subscription: there is no price to show. */
  amountUsd: number | null;
  cycle: BillingCycle | null;
}

/** The cycle suffix on its own, for the shapes that style it apart from the amount. */
function useCycleSuffix(cycle: BillingCycle | null, short: boolean): string {
  const { t } = useTranslation("tradingStrategies");
  if (cycle === "Yearly") {
    return short ? t("price.perYearShort") : t("price.perYear");
  }
  return short ? t("price.perMonthShort") : t("price.perMonth");
}

/**
 * The headline on a strategy or subscription page: the amount carries the weight, the cycle sits
 * beside it in the body colour. Deliberately does not restate the cycle in words — the label above
 * it already says "Monthly price" or "Yearly price".
 */
export function StrategyPriceHeadline({
  free,
  amountUsd,
  cycle,
}: StrategyPriceProps) {
  const { t } = useTranslation("tradingStrategies");
  const suffix = useCycleSuffix(cycle, false);

  if (amountUsd === null || cycle === null) {
    return <>{t("price.staffManaged")}</>;
  }

  if (free) {
    return <>{t("price.free")}</>;
  }

  return (
    <>
      {formatPriceUsd(amountUsd)}
      <span
        className="text-body-secondary"
        style={{ fontSize: "0.95rem", fontWeight: 400 }}
      >
        {" "}
        {suffix}
      </span>
    </>
  );
}

/** The corner of a card: same weighting as the headline, scaled down to sit beside the meta pills. */
export function StrategyPriceCard({
  free,
  amountUsd,
  cycle,
}: StrategyPriceProps) {
  const { t } = useTranslation("tradingStrategies");
  const suffix = useCycleSuffix(cycle, true);

  if (amountUsd === null || cycle === null) {
    return <strong>{t("price.staffManaged")}</strong>;
  }

  if (free) {
    return <strong>{t("price.free")}</strong>;
  }

  return (
    <>
      <strong>{formatPriceUsd(amountUsd)}</strong>
      <span
        className="text-body-secondary fw-normal"
        style={{ fontSize: "0.8rem" }}
      >
        {" "}
        {suffix}
      </span>
    </>
  );
}

/**
 * One run of text, for a definition list, a dropdown row or mid-sentence — anywhere the amount can't
 * be styled apart from its cycle. `compact` gives the "$40/mo" form for tight spaces.
 */
export function StrategyPriceText({
  free,
  amountUsd,
  cycle,
  compact = false,
}: StrategyPriceProps & { compact?: boolean }) {
  const { t } = useTranslation("tradingStrategies");

  if (amountUsd === null || cycle === null) {
    return <>{t("price.staffManaged")}</>;
  }

  if (free) {
    return <>{t("price.free")}</>;
  }

  const amount = formatPriceUsd(amountUsd);
  const key =
    cycle === "Yearly"
      ? compact
        ? "price.yearlyCompact"
        : "price.yearly"
      : compact
        ? "price.monthlyCompact"
        : "price.monthly";

  return <>{t(key, { amount })}</>;
}
